import {
    CalendarClock,
    CalendarX,
    CircleAlert,
    CircleCheck,
    ClipboardCheck,
    OctagonAlert,
    RotateCw,
} from 'lucide-react';
import { useId } from 'react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import InspectionCertificate, { InspectionPhoto } from './InspectionCertificate';
import {
    formatDay,
    formatRoundMonth,
    formatWindow,
    inspectionCardMessage,
    inspectionCardState,
    inspectionOrdinal,
    itemLabel,
    visitsInView,
    type ContractInspections,
    type InspectionCardState,
    type InspectionChecklist,
    type InspectionOpenItem,
} from '@/lib/inspections';

/**
 * The Inspections section: this month's status card, then the contract's inspection record as
 * certificates. It reads `get_contract_inspections` alone and renders what that returns — the
 * attendance, the condition, the hold, the release and every result are the server's. For a
 * driver, every value comes from the lines graded on this contract; nothing of the car's history
 * before it reaches the payload. The record lists this contract's visits only (`visitsInView`).
 */

interface Props {
    inspections: ContractInspections | null | undefined;
    isLoading: boolean;
    isError: boolean;
    onRetry: () => void;
}

type Tone = 'red' | 'amber' | 'emerald' | 'neutral';

const TONE: Record<Tone, { card: string; icon: string; chip: string }> = {
    red: {
        card: 'border-red-200 bg-red-50/60 dark:border-red-900/50 dark:bg-red-950/20',
        icon: 'text-red-600 dark:text-red-400',
        chip: 'border-red-200 bg-red-50 text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300',
    },
    amber: {
        card: 'border-amber-200 bg-amber-50/60 dark:border-amber-900/50 dark:bg-amber-950/20',
        icon: 'text-amber-600 dark:text-amber-400',
        chip: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-300',
    },
    emerald: {
        card: 'border-emerald-200 bg-emerald-50/60 dark:border-emerald-900/50 dark:bg-emerald-950/20',
        icon: 'text-emerald-600 dark:text-emerald-400',
        chip: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/40 dark:text-emerald-300',
    },
    neutral: {
        card: 'border-border bg-card',
        icon: 'text-muted-foreground',
        chip: 'border-border bg-muted text-muted-foreground',
    },
};

const STATE_LOOK: Record<InspectionCardState['kind'], { tone: Tone; Icon: typeof CircleCheck; chip: string | null }> = {
    held: { tone: 'red', Icon: OctagonAlert, chip: 'Held' },
    missed: { tone: 'red', Icon: CalendarX, chip: 'Missed' },
    not_yet: { tone: 'amber', Icon: CalendarClock, chip: 'Not yet' },
    released: { tone: 'emerald', Icon: CircleCheck, chip: 'Released' },
    passed_fix_soon: { tone: 'amber', Icon: CircleAlert, chip: 'Passed – fix soon' },
    passed: { tone: 'emerald', Icon: CircleCheck, chip: 'Passed' },
    not_owed: { tone: 'neutral', Icon: CalendarClock, chip: null },
};

function OpenItemRow({ item, checklist }: { item: InspectionOpenItem; checklist: InspectionChecklist }) {
    const label = itemLabel(checklist, item.item);
    const since = [
        `Open since ${formatDay(item.raised_on)}`,
        item.times_carried > 0 ? inspectionOrdinal(item.times_carried + 1) : null,
    ].filter(Boolean).join(' · ');
    return (
        <li className="space-y-2 rounded-lg border border-border/60 bg-background p-3">
            <p className="text-sm font-medium">{label}</p>
            {item.note && <p className="text-sm">{item.note}</p>}
            <p className="text-xs text-muted-foreground">{since}</p>
            {item.photos.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {item.photos.map((p) => (
                        <InspectionPhoto key={p.id} url={p.url} label={label} className="h-20 w-20" />
                    ))}
                </div>
            )}
        </li>
    );
}

function StatusCard({ state, inspections }: { state: InspectionCardState; inspections: ContractInspections }) {
    const titleId = useId();
    const { current, checklist, open_items: openItems } = inspections;
    const look = STATE_LOOK[state.kind];
    const tone = TONE[look.tone];
    const mustFix = openItems.filter((o) => o.grade === 'fix_now');
    const fixSoon = openItems.filter((o) => o.grade === 'fix_soon');

    const message = inspectionCardMessage(state, visitsInView(inspections));
    const late = (state.kind === 'passed' || state.kind === 'passed_fix_soon') && state.late;
    const openItemRow = (o: InspectionOpenItem) => <OpenItemRow key={o.item} item={o} checklist={checklist} />;

    const { next_opens_on: nextOpensOn, next_closes_on: nextClosesOn } = current;
    const nextWindow = (state.kind === 'released' || state.kind === 'passed' || state.kind === 'passed_fix_soon')
        && nextOpensOn && nextClosesOn
        ? formatWindow(nextOpensOn, nextClosesOn)
        : null;

    return (
        <section aria-labelledby={titleId} className={cn('space-y-4 rounded-xl border p-4 sm:p-5', tone.card)}>
            <div className="flex items-start gap-3">
                <look.Icon className={cn('mt-0.5 h-5 w-5 shrink-0', tone.icon)} aria-hidden="true" />
                <div className="min-w-0 flex-1 space-y-1.5">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <h2 id={titleId} className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            {current.period && state.kind !== 'not_owed'
                                ? `${formatRoundMonth(current.period)} inspection`
                                : 'Monthly inspection'}
                        </h2>
                        {look.chip && (
                            <span className={cn('rounded-full border px-2.5 py-0.5 text-xs font-semibold', tone.chip)}>{look.chip}</span>
                        )}
                    </div>
                    <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm leading-relaxed sm:text-base">
                        <span>{message}</span>
                        {late && (
                            <span className={cn('rounded-full border px-2 py-0.5 text-[11px] font-semibold', TONE.amber.chip)}>Late</span>
                        )}
                    </p>
                </div>
            </div>

            {mustFix.length > 0 && (
                <div className="space-y-2">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Must fix</h3>
                    <ul className="space-y-2">
                        {mustFix.map(openItemRow)}
                    </ul>
                </div>
            )}

            {/* Open Fix soon lines show under every card, so a driver always sees what to fix. Only
                the Passed – fix soon card ties them to the next inspection, as the plan words it;
                under Missed or Not yet that inspection is the one already due. */}
            {fixSoon.length > 0 && (
                <div className="space-y-2">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {state.kind === 'passed_fix_soon' ? 'Fix soon, before your next inspection' : 'Fix soon'}
                    </h3>
                    <ul className="space-y-2">
                        {fixSoon.map(openItemRow)}
                    </ul>
                </div>
            )}

            {nextWindow && (
                <p className="border-t border-border/60 pt-3 text-xs text-muted-foreground">
                    Next inspection {nextWindow}
                </p>
            )}
        </section>
    );
}

export default function ContractInsightInspections({ inspections, isLoading, isError, onRetry }: Props) {
    const recordTitleId = useId();

    if (isLoading) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-28 w-full rounded-xl" />
                <Skeleton className="h-20 w-full rounded-xl" />
                <Skeleton className="h-20 w-full rounded-xl" />
            </div>
        );
    }

    if (isError) {
        return (
            <div className="flex flex-col items-center gap-3 rounded-xl border-2 border-dashed bg-muted/20 py-10 text-center text-muted-foreground">
                <p className="text-sm font-medium">Your inspections could not be loaded.</p>
                <Button variant="outline" size="sm" onClick={onRetry} className="gap-2">
                    <RotateCw className="h-4 w-4" aria-hidden="true" />
                    Try again
                </Button>
            </div>
        );
    }

    const state = inspectionCardState(inspections?.current);
    const visits = inspections ? visitsInView(inspections) : [];

    return (
        <div className="max-w-3xl space-y-6">
            {inspections && state && <StatusCard state={state} inspections={inspections} />}

            <section aria-labelledby={recordTitleId} className="space-y-3">
                <div className="flex items-baseline justify-between gap-3">
                    <h2 id={recordTitleId} className="text-lg font-semibold tracking-tight">Inspection record</h2>
                    {visits.length > 0 && (
                        <span className="text-xs text-muted-foreground tabular-nums">
                            {visits.length} {visits.length === 1 ? 'visit' : 'visits'}
                        </span>
                    )}
                </div>

                {inspections && visits.length > 0 ? (
                    <div className="space-y-3">
                        {/* The newest opens; a refetch that brings a newer visit re-keys both cards,
                            so the new one opens and the old newest closes. */}
                        {visits.map((visit, index) => (
                            <InspectionCertificate
                                key={index === 0 ? `${visit.id}-newest` : String(visit.id)}
                                visit={visit}
                                checklist={inspections.checklist}
                                defaultOpen={index === 0}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="rounded-xl border-2 border-dashed bg-muted/20 py-10 text-center text-muted-foreground">
                        <ClipboardCheck className="mx-auto mb-3 h-8 w-8 opacity-30" aria-hidden="true" />
                        <p className="text-sm font-medium">No inspections on this contract yet</p>
                    </div>
                )}
            </section>
        </div>
    );
}
